import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";

import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { asUtc } from "@/helpers/date";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";

type FeelingAtHomeExportModalProps = ModalBaseProps & {
  startDate?: Date;
  endDate?: Date;
};

export function FeelingAtHomeExportModal({
  startDate,
  endDate,
  isOpened,
  onOpenChange,
}: FeelingAtHomeExportModalProps): React.ReactNode {
  const { t } = useTranslation();

  const analyticsQueries = useAnalyticsQueries();
  const { data, isFetching, error } = useQuery({
    ...analyticsQueries.fahExport({
      startDate: startDate ? asUtc(startDate).toISOString() : undefined,
      endDate: endDate ? asUtc(endDate).toISOString() : undefined,
    }),
    enabled: isOpened,
  });

  return (
    <Modal.Root
      title={t("page.feeling-at-home-details.export.modal.title")}
      description={t("page.feeling-at-home-details.export.modal.description")}
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        {!isFetching && error && (
          <span className="block max-w-search-wide text-text-danger-tertiary">
            {t("page.feeling-at-home-details.export.modal.error")}
          </span>
        )}
      </Modal.Body>

      <Modal.Actions>
        <Button
          isLoading={isFetching}
          disabled={!!error || !data}
          onClick={() => {
            if (!data) {
              return;
            }

            const newBlob = new Blob([data]);

            const blobUrl = window.URL.createObjectURL(newBlob);

            const link = document.createElement("a");
            link.href = blobUrl;

            const translationFileName = t("page.feeling-at-home-details.export.modal.file-name");
            const name = startDate ? "-" + format(startDate, "yyyy-MM") : "";

            link.setAttribute("download", `${translationFileName.replace(/[^a-zA-Z0-9]/g, "")}-${name}.xlsx`);
            document.body.appendChild(link);
            link.click();
            if (link.parentNode) {
              link.parentNode.removeChild(link);
            }

            window.URL.revokeObjectURL(blobUrl);
          }}
        >
          {t("page.feeling-at-home-details.export.modal.button")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
